/**
 * Kind of booking object on one end of a 1:1 tandem wire.
 * Matches weplace `TandemObjectKind` on GET/POST `/plan`.
 */
export enum TandemObjectKind {
  /** Place inventory (workplace, parking, charging, …). */
  PLACE = "place",
  /** Zone inventory. Only conference zones may be wired. */
  ZONE = "zone"
}

/**
 * One undirected tandem pair on the floor plan payload.
 * Either endpoint order is accepted; helpers canonicalize before save.
 * Optional dominant endpoint enables one-way (gate) booking; omit for equal Rule A.
 */
export interface FloorPlanTandemLink {
  /** Kind of the canonical-or-raw left endpoint. */
  leftKind: TandemObjectKind;
  /** Inventory id of the left endpoint. */
  leftInventoryId: number;
  /** Kind of the canonical-or-raw right endpoint. */
  rightKind: TandemObjectKind;
  /** Inventory id of the right endpoint. */
  rightInventoryId: number;
  /**
   * When set, this endpoint is the gate (one-way mode).
   * The other side depends on it. Omit for equal (both block each other).
   */
  dominantKind?: TandemObjectKind | null;
  /** Inventory id of the gate endpoint when `dominantKind` is set. */
  dominantInventoryId?: number | null;
}

/**
 * One selectable booking object in the tandem Autocomplete.
 */
export interface TandemCandidate {
  /** Place or conference zone. */
  kind: TandemObjectKind;
  /** Inventory id used as the wire identity. */
  inventoryId: number;
  /** Inventory display name. */
  name: string;
  /** Product type label (Workplace, Meeting Room, …). */
  typeName: string;
}

/**
 * One end of a tandem wire (kind + inventory id).
 */
export interface TandemEndpoint {
  kind: TandemObjectKind;
  inventoryId: number;
}

/**
 * Booking mode for a tandem wire in Plan Designer.
 * Equal = symmetric Rule A; One-way = optional dominance / gate.
 */
export type TandemBookingMode = "equal" | "oneWay";
